import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { REVIEW_RESIDENT_DIALOG_BUTTON_CLASS } from '../../constants/dialogs'
import { EMPTY_REVIEW_CHECKBOX_FORM } from '../../constants/detailFormDefaults'
import type { ReviewAction, ReviewCheckboxForm } from '../../types'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { residentConfirmPresentation } from '../../constants/residentDialogProps'
import type { ResidentDefaultFormValues } from '../../types/resident'
import { reviewResidentStatus } from '../../services/residentApi'
import { ResidentDialogShell } from './ResidentDialogShell'
import type { ReviewResidentDialogProps } from '../../types'

function formatDocumentStatus(status?: string): string {
  if (!status) return ''
  if (status === 'REJECTED') return 'INCOMPLETE'
  return status
}

function canRejectReview(form: ReviewCheckboxForm, values: ResidentDefaultFormValues): boolean {
  return Boolean(
    (form.lease_aggrement && values.lease_agreement_status !== 'MISSING') ||
      (form.final_payment_ledger && values.payment_ledger_status !== 'MISSING') ||
      (form.legal_forms &&
        ((values.rental_status === 'EVICTION' && values.unlawful_detainer_status !== 'MISSING') ||
          (values.rental_status === 'SKIPPED' && values.skip_claim_affidavit_status !== 'MISSING'))) ||
      (form.notification_date && (!values.rental_status_date || !values.movein_date)),
  )
}

function canApproveReview(form: ReviewCheckboxForm): boolean {
  return (
    form.notification_date ||
    form.lease_aggrement ||
    form.final_payment_ledger ||
    form.legal_forms
  )
}

function canMissingReview(form: ReviewCheckboxForm, values: ResidentDefaultFormValues): boolean {
  return Boolean(
    (form.lease_aggrement && values.lease_agreement_status === 'MISSING') ||
      (form.final_payment_ledger && values.payment_ledger_status === 'MISSING') ||
      (form.legal_forms &&
        ((values.rental_status === 'EVICTION' && values.unlawful_detainer_status === 'MISSING') ||
          (values.rental_status === 'SKIPPED' && values.skip_claim_affidavit_status === 'MISSING'))) ||
      (form.notification_date && (!values.rental_status_date || !values.movein_date)),
  )
}

function ReviewSection({
  id,
  checked,
  onCheckedChange,
  title,
  statusLabel,
  bullets,
}: {
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  title: ReactNode
  statusLabel?: string
  bullets: string[]
}) {
  return (
    <fieldset className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 sm:p-4">
      <label htmlFor={id} className="flex cursor-pointer gap-3">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-button focus:ring-button"
          data-testid={`review-resident-dialog-${id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-snug text-slate-800">
            {title}
            {statusLabel ? (
              <span className="ml-2 font-normal text-slate-600">
                (Current Status — {statusLabel})
              </span>
            ) : null}
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
            {bullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </label>
    </fieldset>
  )
}

/** Mirrors Angular `ReviewResidentComponent` (`Default Review Guidelines` dialog). */
export function ReviewResidentDialog({
  isOpen,
  defaultId,
  formValues,
  isFinalLedger,
  onClose,
  onUpdated,
}: ReviewResidentDialogProps) {
  const [form, setForm] = useState<ReviewCheckboxForm>(EMPTY_REVIEW_CHECKBOX_FORM)
  const [processing, setProcessing] = useState(false)
  const [pendingAction, setPendingAction] = useState<ReviewAction | null>(null)
  const [confirmChoice, setConfirmChoice] = useState<'yes' | 'no' | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setForm(EMPTY_REVIEW_CHECKBOX_FORM)
    setProcessing(false)
    setPendingAction(null)
    setConfirmChoice(null)
  }, [isOpen])

  const leaseStatus = formatDocumentStatus(formValues.lease_agreement_status)
  const paymentStatus = useMemo(() => {
    const base = formatDocumentStatus(formValues.payment_ledger_status)
    const pending =
      ['UPLOADED', 'RECEIVED'].includes(formValues.payment_ledger_status || '') &&
      isFinalLedger !== '1'
    return pending ? `${base} - Pending Final Ledger` : base
  }, [formValues.payment_ledger_status, isFinalLedger])

  const legalStatus = useMemo(() => {
    if (formValues.rental_status === 'EVICTION') {
      return formatDocumentStatus(formValues.unlawful_detainer_status)
    }
    if (formValues.rental_status === 'SKIPPED') {
      return formatDocumentStatus(formValues.skip_claim_affidavit_status)
    }
    return ''
  }, [
    formValues.rental_status,
    formValues.skip_claim_affidavit_status,
    formValues.unlawful_detainer_status,
  ])

  const rejectEnabled = canRejectReview(form, formValues)
  const approveEnabled = canApproveReview(form)
  const missingEnabled = canMissingReview(form, formValues)

  const runSubmit = async (action: ReviewAction, emailSend: boolean) => {
    if (!defaultId) {
      setPendingAction(null)
      setConfirmChoice(null)
      return
    }
    setConfirmChoice(emailSend ? 'yes' : 'no')
    setProcessing(true)
    try {
      await reviewResidentStatus(defaultId, {
        ...form,
        status: action,
        email_send: emailSend,
      })
      trackApplicationEvent('resident_review_submitted', 'resident_record', {
        action_result: action,
      })
      showSuccess('Default Info Updated Successfully!')
      await onUpdated()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
      setPendingAction(null)
      setConfirmChoice(null)
    }
  }

  const requestSubmit = (action: ReviewAction) => {
    if (processing) return
    setPendingAction(action)
  }

  return (
    <>
      <ResidentDialogShell
        isOpen={isOpen}
        title="Default Review Guidelines"
        headerVariant="centeredTitle"
        maxWidth="max-w-3xl"
        bodyScrollOnly
        onClose={onClose}
        panelDataTestId="review-resident-dialog-panel"
        footer={
          <div
            className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-center"
            data-testid="review-resident-dialog-footer"
          >
            <button
              type="button"
              disabled={!rejectEnabled || processing}
              onClick={() => requestSubmit('rejected')}
              data-testid="review-resident-dialog-reject-button"
              className={REVIEW_RESIDENT_DIALOG_BUTTON_CLASS}
            >
              Reject
            </button>
            <button
              type="button"
              disabled={!approveEnabled || processing}
              onClick={() => requestSubmit('received')}
              data-testid="review-resident-dialog-approve-button"
              className={REVIEW_RESIDENT_DIALOG_BUTTON_CLASS}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={!missingEnabled || processing}
              onClick={() => requestSubmit('missing')}
              data-testid="review-resident-dialog-missing-button"
              className={REVIEW_RESIDENT_DIALOG_BUTTON_CLASS}
            >
              Missing
            </button>
          </div>
        }
      >
        <div
          className="space-y-4 text-sm text-slate-900"
          data-testid="review-resident-dialog-body"
        >
          <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3 text-slate-700">
            <p className="italic text-slate-600">
              All documents should be clearly legible in order to process. If not, reject them and ask for a
              better copy to be uploaded.
            </p>
            <p>
              <span className="font-semibold text-slate-800">Reviews:</span> Click the checkboxes to select
              documents or dates, then use Approve, Reject, or Missing to act on documents.
            </p>
          </div>

          <ReviewSection
            id="lease_aggrement"
            checked={form.lease_aggrement}
            onCheckedChange={(lease_aggrement) => setForm((prev) => ({ ...prev, lease_aggrement }))}
            title={
              <>
                <span className="pr-1">1)</span> Lease Agreement:
              </>
            }
            statusLabel={leaseStatus}
            bullets={[
              'Resident/Co-applicant names must be correct',
              'Property name must be correct',
              'Lease Start / End dates must match our Certificate.',
              'Lease can only be for 12 months',
            ]}
          />

          <ReviewSection
            id="final_payment_ledger"
            checked={form.final_payment_ledger}
            onCheckedChange={(final_payment_ledger) =>
              setForm((prev) => ({ ...prev, final_payment_ledger }))
            }
            title={
              <>
                <span className="pr-1">2)</span> Final Payment Ledger:
              </>
            }
            statusLabel={paymentStatus}
            bullets={[
              'Ledger must be for this Lease',
              'Shows security deposit used for damages or to get the unit back on market. Any unused portion should be added to the Partial Payment field to reduce Liberty Rent’s burden.',
              'If partial payments were received, they may be deducted from the Reimbursement amount.',
            ]}
          />

          <ReviewSection
            id="legal_forms"
            checked={form.legal_forms}
            onCheckedChange={(legal_forms) => setForm((prev) => ({ ...prev, legal_forms }))}
            title={
              <>
                <span className="pr-1">3)</span> Legal Forms - Eviction Complaint (UD/Writ) or Skip Reimbursement:
              </>
            }
            statusLabel={legalStatus}
            bullets={[
              'UD/Writ: Must be a valid court document identifying the Resident and Property and clearly shows the date it was filed with the court stamp.',
              'Skip Reimbursement: Must be signed and updated to show loss being reimbursed.',
            ]}
          />

          <ReviewSection
            id="notification_date"
            checked={form.notification_date}
            onCheckedChange={(notification_date) => setForm((prev) => ({ ...prev, notification_date }))}
            title={
              <>
                <span className="pr-1">4)</span> Reimbursement Notification and New Resident Move-In Dates:
              </>
            }
            bullets={['Dates cannot be beyond the Lease End Date.']}
          />

          <div className="rounded-lg border border-slate-200 p-3 sm:p-4">
            <label
              htmlFor="review-resident-notes"
              className="mb-1 block text-sm font-semibold text-slate-800"
            >
              5) Additional Notes
            </label>
            <textarea
              id="review-resident-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              data-testid="review-resident-dialog-notes-textarea"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>
      </ResidentDialogShell>

      <ConfirmDialog
        isOpen={pendingAction !== null}
        title="Send to Property?"
        message="Do you want to send a notice to the Property?"
        primaryLabel="Yes"
        secondaryLabel="No"
        primaryLoading={processing && confirmChoice === 'yes'}
        secondaryLoading={processing && confirmChoice === 'no'}
        disabled={processing}
        onPrimary={() => {
          if (pendingAction && !processing) void runSubmit(pendingAction, true)
        }}
        onSecondary={() => {
          if (pendingAction && !processing) void runSubmit(pendingAction, false)
        }}
        onDismiss={() => {
          if (!processing) setPendingAction(null)
        }}
        {...residentConfirmPresentation}
        overlayDataTestId="review-resident-send-property-overlay"
        primaryButtonDataTestId="review-resident-send-property-yes-button"
        secondaryButtonDataTestId="review-resident-send-property-no-button"
      />
    </>
  )
}
